//----------------------------------------------------------------------------------------------------------------------
// Trigger Property Handler
//----------------------------------------------------------------------------------------------------------------------

import { AbstractMesh, ActionManager, ExecuteCodeAction } from '@babylonjs/core';

import type { GameEventBus } from '../classes/eventBus.ts';
import type { LevelManager } from '../managers/level.ts';
//----------------------------------------------------------------------------------------------------------------------

/**
 * Registers an intersection action (enter or exit) for a trigger mesh against a target mesh.
 */
function registerIntersectionAction(
    actionManager : ActionManager,
    triggerName : string,
    eventBus : GameEventBus,
    targetMesh : AbstractMesh,
    actionType : 'enter' | 'exit'
) : void
{
    const triggerType = actionType === 'enter'
        ? ActionManager.OnIntersectionEnterTrigger
        : ActionManager.OnIntersectionExitTrigger;

    const eventType = `trigger:${ actionType }` as const;

    actionManager.registerAction(
        new ExecuteCodeAction(
            {
                trigger: triggerType,
                parameter: { mesh: targetMesh },
            },
            (evt) =>
            {
                eventBus.publish({
                    type: eventType,
                    payload: {
                        trigger: triggerName,
                        other: evt.source as AbstractMesh,
                    },
                });
            }
        )
    );
}

/**
 * Sets up intersection triggers between a trigger mesh and a target mesh.
 */
function registerTriggersForMesh(
    actionManager : ActionManager,
    triggerName : string,
    eventBus : GameEventBus,
    targetMesh : AbstractMesh
) : void
{
    registerIntersectionAction(actionManager, triggerName, eventBus, targetMesh, 'enter');
    registerIntersectionAction(actionManager, triggerName, eventBus, targetMesh, 'exit');
}

/**
 * Register the trigger property handler.
 *
 * Supports the following property on scene nodes:
 * - `trigger` (string): The trigger zone name
 *
 * When an object enters or exits the trigger zone, events are published:
 * - `trigger:enter` with payload { trigger: string, other: AbstractMesh }
 * - `trigger:exit` with payload { trigger: string, other: AbstractMesh }
 *
 * The trigger mesh is made invisible but retains collision detection.
 * Intersection checks are registered against all non-trigger meshes in the scene,
 * including meshes added after the handler runs (e.g. dynamically spawned entities).
 *
 * @param levelManager
 */
export function registerTriggerHandler(levelManager : LevelManager) : void
{
    levelManager.registerPropertyHandler('trigger', (node, value, level, gameEngine) =>
    {
        const triggerName = value as string;
        const scene = level.scene;

        if(!scene)
        {
            throw new Error('Scene not available for trigger handler');
        }

        if(!(node instanceof AbstractMesh))
        {
            return;
        }

        const eventBus = gameEngine.eventBus;

        // Make the trigger invisible but keep collision
        node.isVisible = false;
        node.checkCollisions = true;

        // Create action manager if needed
        const actionManager = (node.actionManager as ActionManager) ?? new ActionManager(scene);
        node.actionManager = actionManager;

        // Register intersection triggers against all existing non-trigger meshes
        for(const mesh of scene.meshes)
        {
            if(mesh !== node && !mesh.metadata?.trigger)
            {
                registerTriggersForMesh(actionManager, triggerName, eventBus, mesh);
            }
        }

        // Register triggers for meshes added later (e.g. dynamically spawned entities)
        const observer = scene.onNewMeshAddedObservable.add((mesh) =>
        {
            if(mesh !== node && !mesh.metadata?.trigger)
            {
                registerTriggersForMesh(actionManager, triggerName, eventBus, mesh);
            }
        });

        // Clean up observer when the trigger mesh is disposed
        node.onDisposeObservable.add(() =>
        {
            scene.onNewMeshAddedObservable.remove(observer);
        });
    });
}

//----------------------------------------------------------------------------------------------------------------------
